import React, { useState, useEffect, memo, useRef } from 'react';
import { Trans, t } from '@lingui/macro';
import { CSSTransition } from 'react-transition-group';
import { ChevronDown, ChevronRight, ChevronUp, Heart, Loader2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { UtilApp } from '../../services/Utils/UtilApp';
import { Util } from '../../services/Utils/Util';
import { My } from '../../services/My';
import { Fire } from '../../services/FireClient';
import { MyNotifs } from '../../services/MyNotifs';
import { Message, SendFile } from '../../models/Message';
import { Room } from '../../models/Room';
import { UIMessage } from '../components/UIMessage';
import MessageItem, { MessageItemState } from '../components/MessageItem';
import MsgInputFooterReactions from '../components/message-inputs/MsgInputFooterReactions';
import { Badge } from '../../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';

interface RoomMessageItemProps {
    message: UIMessage;
    room: Room;
    activeThreads: Set<string>;
    toggleActiveThread: (id: string) => void;
    highlightedId: string | null;
    highlightedReaction?: { messageId: string; emoticon: string } | null;
    currentMessage: UIMessage | null;
    messageRefs: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>;
    getNodeRef: (id: string) => React.RefObject<HTMLDivElement>;
    getMessageItemState: (m: UIMessage) => MessageItemState;
    onMessageClick: (m: UIMessage) => void;
    progressForFile: { [key: string]: SendFile };
    onResendMessage: (m: UIMessage) => void;
    mentionTarget: { user: any, messageId: string, threadId: string } | null;
    setMentionTarget: (target: { user: any, messageId: string, threadId: string } | null) => void;
    forceUpdate: () => void;
    onTargetMessage: (messageId: string | null, parentMessageId: string | null) => void;
    onLoadChildren: (parentId: string, children: Message[]) => void;
}

function RoomMessageItemInner({
    message,
    room,
    activeThreads,
    toggleActiveThread,
    highlightedId,
    highlightedReaction,
    currentMessage,
    messageRefs,
    getNodeRef,
    getMessageItemState,
    onMessageClick,
    progressForFile,
    onResendMessage,
    mentionTarget,
    setMentionTarget,
    forceUpdate,
    onTargetMessage,
    onLoadChildren
}: RoomMessageItemProps) {
    const [activeReplyId, setActiveReplyId] = useState<string | null>(null);
    const [loadingReplies, setLoadingReplies] = useState(false);
    const [showReactionPopover, setShowReactionPopover] = useState(false); // 🔧 리액션 팝오버 상태
    const clearedNotifsRef = useRef<Set<string>>(new Set()); // 🔧 이미 클리어한 알림 추적

    const isThreadExpanded = activeThreads.has(message.msg.id);




    const handleReplyToClick = (targetId: string, currentId: string) => {
        // 1. Check if target is the main message itself
        if (targetId === message.msg.id) {
            const mainRef = getNodeRef(message.msg.id);
            if (mainRef.current) {
                mainRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                const element = mainRef.current.querySelector('.message-box');
                if (element) {
                    element.classList.add('bg-orange-100', 'ring-2', 'ring-orange-400');
                    setTimeout(() => element.classList.remove('bg-orange-100', 'ring-2', 'ring-orange-400'), 2000);
                }
            }
            return;
        }

        // 2. Check if target is in the current thread replies
        const targetReply = replies.find(r => r.msg.id === targetId);
        if (targetReply) {
            const targetRef = getNodeRef(targetId);
            if (targetRef.current) {
                targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });

                // Add highlight effect to the inner message container if possible, or the wrapper
                // The wrapper has the ref, so we add classes there or find the inner wrapper
                targetRef.current.classList.add('bg-orange-100', 'ring-2', 'ring-orange-400');
                setTimeout(() => targetRef.current?.classList.remove('bg-orange-100', 'ring-2', 'ring-orange-400'), 2000);
            }
            return;
        }

        // 3. Not found locally, delegate to parent logic
        onTargetMessage(targetId, message.msg.id);
    };

    const nodeRef = getNodeRef(message.msg.id);
    const replies = message.nonReactions();
    // 리액션을 제외한 답글 수 사용
    // children이 로드되지 않은 경우(length === 0)에만 message.replyCount 사용
    const replyCount = replies.length > 0 ? replies.length : (message.replyCount ?? 0);

    // lastReply derived from either loaded children or summary data
    const lastReplyMsg = message.lastReply || (replies.length > 0 ? replies[replies.length - 1].msg : null);

    // 🔧 MyNotifs에서 해당 스레드의 읽지 않은 알림 개수 계산
    // (스레드가 로드되지 않은 상태에서도 알림 기반으로 표시)
    const unreadFromNotifs = (() => {
        const roomNotifs = MyNotifs.NotificationsByRoom.get(room.id) ?? [];
        return roomNotifs.filter(n =>
            n.isNew &&
            n.parentMessageId === message.msg.id &&
            n.type !== 'Emoticon'
        ).length;
    })();

    // 기존 unreadReplyCount와 알림 기반 개수 중 큰 값 사용
    const totalUnread = Math.max(message.msg.unreadReplyCount ?? 0, unreadFromNotifs);

    const isAutoMessage = !!message.msg.autoMessage;

    // Handle jump to reply from outside (e.g. notification)
    useEffect(() => {
        if (!highlightedId) return;

        // If highlightedId is one of the loaded replies, scroll into view
        const isTargetInReplies = replies.some(r => r.msg.id === highlightedId);

        if (isThreadExpanded && isTargetInReplies) {
            setTimeout(() => {
                const targetRef = getNodeRef(highlightedId);
                if (targetRef.current) {
                    targetRef.current.scrollIntoView({ behavior: 'auto', block: 'center' });
                }
            }, 100);
        }
    }, [highlightedId, isThreadExpanded, replies]);

    // Lazy load replies when expanded
    useEffect(() => {
        const isLoaded = message.repliesLoaded || message.msg.repliesLoaded;

        if (isThreadExpanded && !isLoaded && !loadingReplies) {
            setLoadingReplies(true);
            Fire.msg.getChildrenMessages(My.user.id, message.msg.id).then((msgs: Message[]) => {
                onLoadChildren(message.msg.id, msgs);
                // We set internal loading to false, but the "isLoaded" check above
                // depends on the new prop "message" arriving from RoomContent
                setLoadingReplies(false);
            }).catch((e: Error) => {
                setLoadingReplies(false);
            });
        }

        // Mark as read if expanded
        if (isThreadExpanded && message.msg.unreadReplyCount && message.msg.unreadReplyCount > 0) {
            message.msg.unreadReplyCount = 0;
            forceUpdate();
        }

        // 🔧 스레드 펼침 시 신규 댓글 알림 읽음 처리 (중복 방지)
        if (isThreadExpanded && replies.length > 0) {
            const newReplyNotifs = replies
                .filter(reply => reply.isNew())
                .map(reply => MyNotifs.NotificationsByMessage.get(reply.msg.id))
                .filter((notif): notif is NonNullable<typeof notif> =>
                    notif != null && notif.isNew && !clearedNotifsRef.current.has(notif.id)
                );

            if (newReplyNotifs.length > 0) {
                newReplyNotifs.forEach(n => clearedNotifsRef.current.add(n.id));
                Fire.clearNotifications(newReplyNotifs);
            }
        }
    }, [isThreadExpanded, message.repliesLoaded, message.msg.repliesLoaded, message.msg.unreadReplyCount, loadingReplies, replies]);

    const loadedReplyUsers = replies.reduce((acc, reply) => {
        if (reply?.msg?.user && !acc.find(u => u.id === reply.msg.user.id)) {
            acc.push(reply.msg.user);
        }
        return acc;
    }, [] as { id: string; name: string; photoUrl: string | null }[]);

    const replyUsers = loadedReplyUsers.length > 0 ? loadedReplyUsers.slice(0, 3) : (lastReplyMsg?.user ? [lastReplyMsg.user] : []);

    return (
        <div className="message-root md:px-4" id={`ref-${message.msg.id}`}>
            {message.isNewDay && (
                <div className="flex flex-col items-center">
                    <span className="flex flex-col items-center px-3 md:py-1 md:mb-1 rounded-[20px] md:border border-[#E5E5E5] md:bg-gray-900/5 text-[#555] text-[12px] font-semibold">
                        {UtilApp.dateStrToDay(message.msg.created, My.user.lang)}
                    </span>
                </div>
            )}
            <div
                className={cn(
                    "flex flex-col py-2 px-3 md:rounded-[12px] scroll-m-4 mb-2 transition-all relative border border-transparent",
                    !isAutoMessage && "cursor-pointer",
                    isThreadExpanded ? "bg-gray-100 border-slate-200 shadow-sm" : (isAutoMessage ? "" : "bg-gray-50 border-transparent")
                )}
                role='article'
                data-message-id={message.msg.id}
            >
                {/* Main Message */}
                <CSSTransition
                    nodeRef={nodeRef}
                    in={message.msg.id === highlightedId}
                    timeout={2000}
                    classNames="flash"
                    unmountOnExit={false}
                >
                    <div
                        ref={(el) => {
                            (nodeRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                            messageRefs.current[message.msg.id] = el;
                        }}
                        className={cn(
                            "message-box rounded-[8px] transition-colors p-2 border-2 border-transparent",
                            currentMessage?.msg.id === message.msg.id && "bg-orange-50 border-orange-300",
                            message.msg.id === highlightedId && "bg-orange-100 border-orange-400"
                        )}
                    >
                        <MessageItem
                            m={message}
                            state={getMessageItemState(message)}
                            onMessageClick={onMessageClick}
                            progressForFile={progressForFile}
                            onResendMessage={onResendMessage}
                            highlightedReaction={highlightedReaction}
                            reactionSlot={!isAutoMessage ? (
                                <Popover open={showReactionPopover} onOpenChange={setShowReactionPopover}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className={cn(
                                                "h-7 w-7 p-0 text-gray-400 hover:text-pink-500 hover:bg-pink-50 rounded-full transition-colors",
                                                showReactionPopover && "text-pink-500 bg-pink-50"
                                            )}
                                        >
                                            <Heart className="w-4 h-4" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        className="p-3 bg-gradient-to-br from-pink-50 to-rose-50 border-2 border-pink-200 shadow-xl shadow-pink-100/50 rounded-xl w-auto"
                                        side="right"
                                        align="start"
                                        sideOffset={4}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <MsgInputFooterReactions room={room} parentMessage={message} onReactionUpdate={() => { forceUpdate(); setShowReactionPopover(false); }} />
                                    </PopoverContent>
                                </Popover>
                            ) : undefined}
                        />
                    </div>
                </CSSTransition>

                {/* Thread Indicator - 부모 박스 안에서 표시 */}
                {
                    (replyCount > 0 || isThreadExpanded) && (
                        <div className="ml-2 xs:ml-[52px]">
                            <div
                                className="flex items-center gap-3 group/thread cursor-pointer py-1"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleActiveThread(message.msg.id);
                                }}
                            >
                                {replyCount > 0 ? (
                                    <>
                                        <div className="flex -space-x-1.5">
                                            {replyUsers.map((user, idx) => (
                                                <Avatar key={user.id} className="w-5 h-5 border-2 border-white rounded-[6px]">
                                                    <AvatarImage src={user.photoUrl || Util.ANONYMOUS_PHOTO_URL} className="rounded-[6px]" />
                                                    <AvatarFallback className="text-[8px] font-semibold bg-orange-100 text-orange-600 rounded-[6px]">
                                                        {user.name?.[0]}
                                                    </AvatarFallback>
                                                </Avatar>
                                            ))}
                                        </div>
                                        <span className="text-xs font-bold text-orange-500 group-hover/thread:underline">
                                            {replyCount} {replyCount === 1 ? t`reply` : t`replies`}
                                        </span>
                                        {lastReplyMsg && (
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-[10px] text-gray-400 font-medium group-hover/thread:text-gray-500">
                                                    {t`Last reply`} {UtilApp.dateStrToTimeAndDay(lastReplyMsg.created, My.user.lang)}
                                                </span>
                                                {totalUnread > 0 && (
                                                    <Badge className="h-4 min-w-[1rem] px-1 flex items-center justify-center text-[9px] bg-orange-500 text-white rounded-full border-none">
                                                        {totalUnread}
                                                    </Badge>
                                                )}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <span className="text-xs font-bold text-gray-400">Reply</span>
                                )}
                                {isThreadExpanded ? (
                                    <ChevronUp className="w-4 h-4 text-gray-400" />
                                ) : (
                                    <ChevronDown className="w-4 h-4 text-gray-400" />
                                )}
                            </div>

                            {/* Expanded Thread Area */}
                            {isThreadExpanded && (
                                <div className="mt-3 pl-2 border-l-2 border-gray-200">
                                    {loadingReplies && (
                                        <div className="flex items-center gap-2 py-2 text-gray-400 italic text-xs">
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                            <span>{t`Loading replies...`}</span>
                                        </div>
                                    )}
                                    {/* Replies - 하트 버튼 방식 */}
                                    {replies.map((reply) => {
                                        const replyNodeRef = getNodeRef(reply.msg.id);
                                        const isHighlighted = highlightedId === reply.msg.id;
                                        const isReplyReactionOpen = activeReplyId === reply.msg.id;
                                        const isSelected = currentMessage?.msg.id === reply.msg.id;

                                        return (
                                            <div key={reply.msg.id} className="mb-3">
                                                <div
                                                    className={cn(
                                                        "relative rounded-lg transition-colors border-2 border-transparent cursor-pointer p-2",
                                                        isSelected && "bg-orange-50 border-orange-300",
                                                        isHighlighted && "bg-orange-100 border-orange-400"
                                                    )}
                                                    ref={replyNodeRef}
                                                >
                                                    <MessageItem
                                                        m={reply}
                                                        state={getMessageItemState(reply)}
                                                        onMessageClick={onMessageClick}
                                                        progressForFile={progressForFile}
                                                        onResendMessage={onResendMessage}
                                                        onReplyToClick={(targetId) => handleReplyToClick(targetId, reply.msg.id)}
                                                        reactionSlot={(
                                                            <Popover
                                                                open={isReplyReactionOpen}
                                                                onOpenChange={(open) => setActiveReplyId(open ? reply.msg.id : null)}
                                                            >
                                                                <PopoverTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className={cn(
                                                                            "h-7 w-7 p-0 text-gray-400 hover:text-pink-500 hover:bg-pink-50 rounded-full transition-colors",
                                                                            isReplyReactionOpen && "text-pink-500 bg-pink-50"
                                                                        )}
                                                                    >
                                                                        <Heart className="w-4 h-4" />
                                                                    </Button>
                                                                </PopoverTrigger>
                                                                <PopoverContent
                                                                    className="p-3 bg-gradient-to-br from-pink-50 to-rose-50 border-2 border-pink-200 shadow-xl shadow-pink-100/50 rounded-xl w-auto"
                                                                    side="right"
                                                                    align="start"
                                                                    sideOffset={4}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <MsgInputFooterReactions room={room} parentMessage={reply} onReactionUpdate={() => { forceUpdate(); setActiveReplyId(null); }} />
                                                                </PopoverContent>
                                                            </Popover>
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {/* 🔧 스레드 하단 답글 입력 제거 - 메인 입력창(MsgInputFooter)에서 처리 */}
                                </div>
                            )}
                        </div>
                    )
                }

            </div>

            {/* Legacy Children */}
            {
                replyCount === 0 && message.children.length > 0 && message.nonReactions().map((c) => {
                    const childNodeRef = getNodeRef(c.msg.id);
                    return (
                        <CSSTransition
                            key={`child-${c.msg.id}`}
                            nodeRef={childNodeRef}
                            in={c.msg.id === highlightedId}
                            timeout={2000}
                            classNames="flash"
                            unmountOnExit={false}
                        >
                            <div
                                ref={(el) => {
                                    (childNodeRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                                    messageRefs.current[c.msg.id] = el;
                                }}
                                className={cn(
                                    "message-box bg-gray-50 rounded-[8px] transition-colors mt-2",
                                    currentMessage === c && "pl-2 bg-orange-100"
                                )}
                            >
                                <MessageItem m={c} state={getMessageItemState(c)} onMessageClick={onMessageClick} parent={message} progressForFile={progressForFile} onResendMessage={onResendMessage} />
                            </div>
                        </CSSTransition>
                    );
                })
            }
        </div >
    );
}

// React.memo with custom comparison to prevent unnecessary re-renders during scroll
export const RoomMessageItem = memo(RoomMessageItemInner, (prevProps, nextProps) => {
    // Return true if props are equal (skip re-render)
    // Return false if props are different (re-render needed)

    // Core message identity - always check
    if (prevProps.message.msg.id !== nextProps.message.msg.id) return false;

    // 🔧 isLocal 상태 변경 체크 (스피너 표시/숨김)
    if (prevProps.message.isLocal !== nextProps.message.isLocal) return false;

    // Message content changes
    if (prevProps.message.msg.text !== nextProps.message.msg.text) return false;
    if (prevProps.message.children.length !== nextProps.message.children.length) return false;
    if (prevProps.message.msg.unreadReplyCount !== nextProps.message.msg.unreadReplyCount) return false;
    if (prevProps.message.repliesLoaded !== nextProps.message.repliesLoaded) return false;

    // 🔧 자식 메시지의 isLocal 상태 변경 체크
    for (let i = 0; i < prevProps.message.children.length; i++) {
        if (prevProps.message.children[i]?.isLocal !== nextProps.message.children[i]?.isLocal) {
            return false;
        }
    }

    // Highlight state - important for visual feedback
    const wasHighlighted = prevProps.highlightedId === prevProps.message.msg.id;
    const isHighlighted = nextProps.highlightedId === nextProps.message.msg.id;
    if (wasHighlighted !== isHighlighted) return false;

    // Check if any child is highlighted
    const prevChildHighlighted = prevProps.message.children.some(c => c.msg.id === prevProps.highlightedId);
    const nextChildHighlighted = nextProps.message.children.some(c => c.msg.id === nextProps.highlightedId);
    if (prevChildHighlighted !== nextChildHighlighted) return false;

    // Thread expansion state
    const wasExpanded = prevProps.activeThreads.has(prevProps.message.msg.id);
    const isExpanded = nextProps.activeThreads.has(nextProps.message.msg.id);
    if (wasExpanded !== isExpanded) return false;

    // Current message selection (부모 메시지)
    const wasSelected = prevProps.currentMessage?.msg.id === prevProps.message.msg.id;
    const isSelected = nextProps.currentMessage?.msg.id === nextProps.message.msg.id;
    if (wasSelected !== isSelected) {
        return false;
    }

    // 🔧 스레드 내 답글의 선택 상태 변경 감지 (어떤 자식이 선택되었는지 ID로 비교)
    const prevSelectedChildId = prevProps.message.children.find(c => c.msg.id === prevProps.currentMessage?.msg.id)?.msg.id;
    const nextSelectedChildId = nextProps.message.children.find(c => c.msg.id === nextProps.currentMessage?.msg.id)?.msg.id;
    if (prevSelectedChildId !== nextSelectedChildId) {
        return false;
    }

    // Highlighted reaction
    if (prevProps.highlightedReaction?.messageId !== nextProps.highlightedReaction?.messageId) return false;
    if (prevProps.highlightedReaction?.emoticon !== nextProps.highlightedReaction?.emoticon) return false;

    // Mention target (only check if this message is involved)
    const prevMention = prevProps.mentionTarget?.threadId === prevProps.message.msg.id;
    const nextMention = nextProps.mentionTarget?.threadId === nextProps.message.msg.id;
    if (prevMention !== nextMention) return false;

    // Room changes (rare but important)
    if (prevProps.room.id !== nextProps.room.id) return false;

    // All important props are equal, skip re-render
    return true;
});
